// src/pages/orders/OrderDetailPage/OrderDetailPage.jsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import OrdersAPI from '../../../api/v3/orders-api-v3';
import ApplyVoucherWidget from '../../../components/orders/ApplyVoucherWidget';

const BASE = '/app/sales/bagspa';

// ── Pricing rules ──────────────────────────────────────────────────────────────
const MARKUP_REGULAR = 250_000;
const PRIORITY_FACTOR = 1.5;

// ── Tooltips ──────────────────────────────────────────────────────────────────
const TIPS = {
  initial: 'Initial Price: harga taksiran admin sebelum dapat harga dari vendor',
  final: 'Final Price: harga final dari vendor setelah dilakukan inspeksi/ tes noda dari vendor',
  totalInitial: 'Total Initial Price: total dari semua item harga taksiran admin sebelum dapat harga dari vendor',
  totalFinal: 'Total Final Price: total dari semua item harga final dari vendor setelah dilakukan inspeksi/ tes noda dari vendor',
  advance: 'Advance Payment: harga pembayaran awal dari customer pada Customer Receipt yang diberikan',
  outstanding: 'Outstanding balance: selisih harga dari Total Final Price - Advance Payment, yakni harga yang harus dibayarkan customer pada saat pengambilan barang atau H+1 (sesuai kesepakatan)',
  initialColHint: 'Initial Price: harga taksiran admin sebelum dapat harga dari vendor.',
  finalColHint: 'Final Price (admin base): isi base final dari vendor. Sistem otomatis hitung charged (base + 250k; Priority x1.5).',
};

// ── Notes helpers ─────────────────────────────────────────────────────────────
const NOTES_MARK = '> ';
function ensureLeadingMarker(text) {
  return (text || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => {
      const t = line.trim();
      if (!t) return '';
      return /^>\s?/.test(t) ? t.replace(/^>\s?/, '> ') : NOTES_MARK + t;
    })
    .join('\n');
}
function countInvalidNoteLines(text) {
  return (text || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim() && !/^>\s?/.test(l.trim()))
    .length;
}

// Minimal tooltip
const InfoTip = ({ title, side = 'bottom', maxWidth = 360 }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
const updatePos = useCallback(() => {
  const el = btnRef.current;
  if (!el) return;

  const r = el.getBoundingClientRect();
  const gap = 8;
  const rawTop = side === 'top' ? r.top - gap : r.bottom + gap;
  const rawLeft = r.left;
  const margin = 8;
  const left = Math.max(margin, Math.min(rawLeft, window.innerWidth - (maxWidth + margin)));
  const top = Math.max(margin, rawTop);

  setPos({ top, left });
}, [side, maxWidth]);
  useEffect(() => {
    if (!open) return;
    updatePos();
    const onClickOutside = (e) => {
      if (!btnRef.current) return;
      const bubble = document.getElementById('__infotip-bubble');
      if (!btnRef.current.contains(e.target) && !(bubble && bubble.contains(e.target))) setOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    const onReflow = () => updatePos();
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, updatePos]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="btn btn-link p-0 ml-1"
        onClick={() => setOpen((v) => !v)}
        aria-label="Info"
        aria-expanded={open}
        style={{ textDecoration: 'none', lineHeight: 1 }}
      >
        <span style={{
          display: 'inline-flex', width: 18, height: 18, borderRadius: '50%',
          border: '1px solid rgba(0,0,0,.35)', color: '#555', fontWeight: 700,
          alignItems: 'center', justifyContent: 'center', fontSize: 12, userSelect: 'none',
        }}>i</span>
      </button>
      {open && createPortal(
        <div
          id="__infotip-bubble"
          role="tooltip"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 2000,
            background: '#fff', border: '1px solid rgba(0,0,0,.12)',
            boxShadow: '0 4px 16px rgba(0,0,0,.12)', borderRadius: 8,
            padding: '10px 12px', fontSize: 12, lineHeight: 1.4, maxWidth, minWidth: 220,
          }}
        >
          {title}
        </div>,
        document.body
      )}
    </>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtIDR(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return 'Rp ' + Number(n).toLocaleString('id-ID');
}
function toYmd(d) { return d ? dayjs(d).format('YYYY-MM-DD') : ''; }
const digits = (s) => (s || '').toString().replace(/\D+/g, '');
const toInt = (s) => Number(digits(s)) || 0;
const fmtThousands = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);
const normalizeMoneyInput = (raw) => {
  const n = toInt(raw);
  return { n, text: n ? fmtThousands(n) : '' };
};
function applyMarkup(base, type) {
  const reg = (Number(base) || 0) + MARKUP_REGULAR;
  return Math.round(type === 'priority' ? reg * PRIORITY_FACTOR : reg);
}
function baseFromCharged(charged, type) {
  const val = Number(charged) || 0;
  if (type === 'priority') return Math.max(0, Math.round(val / PRIORITY_FACTOR - MARKUP_REGULAR));
  return Math.max(0, Math.round(val - MARKUP_REGULAR));
}

export default function OrderDetailPage() {
  const { id } = useParams();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [flash,   setFlash]   = useState('');

  const [notes, setNotes] = useState('');
  const notesRef = useRef(null);
  const [notesError, setNotesError] = useState('');
  const [deliveryCostText, setDeliveryCostText] = useState('');
  const [estimatedCompletion, setEstimatedCompletion] = useState('');

  const [rows, setRows] = useState([]);

  const reload = useCallback(async () => {
    const json = await OrdersAPI.show(id);
    const o = json?.item || json?.data || json;
    setOrder(o);

    setNotes(o.additional_notes ?? '');
    setDeliveryCostText(o.delivery_cost ? fmtThousands(o.delivery_cost) : '');
    setEstimatedCompletion(o.estimated_completion_date ? toYmd(o.estimated_completion_date) : '');

    // Build rows: prefer server's final_vendor_price for editable base.
    const mapped = (o.order_items || []).map((it) => {
      const ot = it.order_type === 'priority' ? 'priority' : 'regular';

      // Initial (charged) comes from server fixed/charged value.
      const initCharged = Number(
        it.initial_fixed_price ?? it.initial_price ?? 0
      ) || 0;

      // Final base: use final_vendor_price if present; else reconstruct from charged.
      const finalBase =
        (it.final_vendor_price != null ? Number(it.final_vendor_price) : null) ??
        (it.final_fixed_price != null
          ? baseFromCharged(Number(it.final_fixed_price), ot)
          : 0);

      return {
        id: it.id,
        sku: it.sku,
        brand_name: it.brand_name,
        category_name: it.category_name,
        colour_name: it.colour_name,
        material_name: it.material_name,
        order_category_name:
          it.order_category_name || it.order_category?.name || it.order_category_title || '—',
        order_type: ot,
        initial_charged: initCharged,
        final_base_text: finalBase ? finalBase.toLocaleString('id-ID') : '',
      };
    });
    setRows(mapped);
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await reload();
      } catch (e) {
        setError(e.message || 'Failed to load order.');
      } finally {
        setLoading(false);
      }
    })();
  }, [reload]);

  // Charged (customer) final for a row = markup(final_vendor_price).
  // IMPORTANT: if base is empty/0, charged should be 0 (no accidental +250k).
  const effectiveFinal = (r) => {
    const base = toInt(r.final_base_text);
    if (!base) return 0;
    return applyMarkup(base, r.order_type);
  };

  // Pre-discount totals (items only). Server will subtract any voucher on save.
  const totals = useMemo(() => {
    const init = rows.reduce((s, r) => s + (Number(r.initial_charged) || 0), 0);
    const fin  = rows.reduce((s, r) => s + effectiveFinal(r), 0);
    return { init, fin, adv: init };
  }, [rows]);

  // Voucher math for display
  const voucherDiscount = Number(order?.voucher?.discount_amount || 0);
  const finalAfterDiscount = Math.max(0, (totals.fin || 0) - voucherDiscount);
  const outstanding = useMemo(
    () => Math.max(0, finalAfterDiscount - (totals.adv || 0)),
    [finalAfterDiscount, totals.adv]
  );

  async function saveAll() {
    if (!order) return;
    setError('');
    setFlash('');

    // enforce notes format
    const fixedNotes = ensureLeadingMarker(notes);
    const bad = countInvalidNoteLines(fixedNotes);
    if (bad) {
      setNotesError(`Ada ${bad} baris tanpa awalan "> ".`);
      notesRef.current?.focus();
      return;
    }

    try {
      setSaving(true);

      const payload = {
        additional_notes: fixedNotes,
        delivery_cost: toInt(deliveryCostText),
        estimated_completion_date: estimatedCompletion || null,

        // header totals (pre-discount; server will recompute voucher-aware net from items)
        initial_fixed_price: Number(totals.init) || 0,
        final_fixed_price:   Number(totals.fin)  || 0,
        advance_payment_price: Number(totals.adv) || 0,

        // per-item: ALWAYS send both final_vendor_price and final_fixed_price
        order_items_attributes: rows.map((r) => {
          const finBase  = toInt(r.final_base_text); // admin/vendor base
          const finFixed = effectiveFinal(r);        // charged (with markup)
          return {
            id: r.id,
            order_type: r.order_type,
            quantity: 1,
            final_vendor_price: finBase,
            final_fixed_price:  finFixed,
            // (we keep initial values read-only on this page)
          };
        }),
      };

      await OrdersAPI.update(order.id, payload);

      const refreshed = await OrdersAPI.show(order.id);
      const o = refreshed?.item || refreshed?.data || refreshed;
      setOrder(o);
      setFlash('Saved successfully.');
    } catch (e) {
      setError(e.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div>Loading…</div>;
  if (error)   return <div className="alert alert-danger">{error}</div>;
  if (!order)  return <div>No order found.</div>;

  const customer = order.customer || {};
  const saName   = order?.sales_assistant?.first_name || order?.sales_assistant_name;
  const saEmail  = order?.sales_assistant?.email;
  const assistBy = order?.assist_by;
  const assistByLabel = (() => {
    if (!assistBy) return '—';

    if (typeof assistBy === 'string') {
      return assistBy === 'Not_Available' ? 'Not Available' : assistBy;
    }

    return (
      assistBy?.label ||
      assistBy?.first_name ||
      assistBy?.name ||
      assistBy?.email ||
      '—'
    );
  })();

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">
          Order — {order.order_no || `#${order.id}`}
          {order.voucher?.code && (
            <span className="badge bg-success ms-2">
              Voucher {order.voucher.code}
              {Number(order.voucher.discount_amount) > 0 &&
                ` — ${fmtIDR(order.voucher.discount_amount)}`}
            </span>
          )}
        </h2>
        <div className="d-flex" style={{ gap: 8 }}>
          <Link to={`${BASE}/orders`} className="btn btn-light">&larr; Back</Link>
          <button className="btn btn-primary" onClick={saveAll} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {flash && <div className="alert alert-success py-2">{flash}</div>}

      {/* Sales Assistant */}
      <div className="alert alert-secondary py-2">
        <div>
          <b>Sales Assistant:</b>{' '}
          <span className="ml-1">
            {saName || '—'} {saEmail ? `- ${saEmail}` : ''}
          </span>
        </div>

        <div>
          <b>Assist By:</b>{' '}
          <span className="ml-1">{assistByLabel}</span>
        </div>
      </div>

      {/* Summary */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            {/* Left */}
            <div className="col-md-6">
              <div><b>Customer:</b> {customer.name || '—'} ({customer.email || '—'})</div>
              <div><b>Phone:</b> {customer.phone || '—'}</div>
              <div><b>Created:</b> {dayjs(order.created_at).format('DD MMM YYYY')}</div>

              <div className="mt-2">
                <label className="mb-1"><b>Estimated Completion</b></label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={estimatedCompletion}
                  onChange={(e) => setEstimatedCompletion(e.target.value)}
                />
              </div>
            </div>

            {/* Right */}
            <div className="col-md-6">
              <div className="mt-2">
                <label className="mb-1"><b>Delivery Cost</b></label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control form-control-sm"
                  value={deliveryCostText}
                  onChange={(e) => setDeliveryCostText(normalizeMoneyInput(e.target.value).text)}
                  placeholder="100.000"
                />
              </div>

              {order && (
                <div className="mt-3">
                  <ApplyVoucherWidget
                    orderId={order.id}
                    appliedCode={order.voucher?.code}
                    appliedDiscount={order.voucher?.discount_amount}
                    disabled={!!order.voucher}
                    onApplied={() => reload()}
                  />
                </div>
              )}

              <div className="mt-2 small text-muted">
                Final total includes <b>+Rp {MARKUP_REGULAR.toLocaleString('id-ID')}</b> per item
                (Priority = <b>x{PRIORITY_FACTOR}</b> on top of that).
              </div>

              <div className="mt-2">
                <b>Advance Payment <InfoTip title={TIPS.advance} />:</b> {fmtIDR(totals.adv)}
              </div>

              {voucherDiscount > 0 && (
                <div className="mt-1">
                  <b>Discount (Voucher):</b> −{fmtIDR(voucherDiscount)}
                </div>
              )}

              <div className="mt-1">
                <b>Total Initial Price <InfoTip title={TIPS.totalInitial} />:</b> {fmtIDR(totals.init)}
              </div>

              <div className="mt-1">
                <b>Total Final Price <InfoTip title={TIPS.totalFinal} />:</b> {fmtIDR(finalAfterDiscount)}
              </div>

              <div className="mt-1">
                <b>Outstanding Balance <InfoTip title={TIPS.outstanding} />:</b> {fmtIDR(outstanding)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="card mb-3">
        <div className="card-body">
          <label className="mb-1 d-flex align-items-center justify-content-between">
            <b>Notes</b>
            <small className="text-muted">
              Format: tiap baris harus diawali <code>&gt; </code>. Tekan <b>Enter</b> akan otomatis menambah <code>&gt; </code>.
            </small>
          </label>

          <textarea
            ref={notesRef}
            className={`form-control ${notesError ? 'is-invalid' : ''}`}
            rows={4}
            value={notes}
            onChange={(e) => {
              const val = e.target.value;
              setNotes(val);
              const bad = countInvalidNoteLines(val);
              setNotesError(bad ? `Ada ${bad} baris tanpa awalan "> ".` : '');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const el = notesRef.current;
                if (!el) return;
                const { selectionStart, selectionEnd, value } = el;
                const insert = '\n' + NOTES_MARK;
                const newValue = value.slice(0, selectionStart) + insert + value.slice(selectionEnd);
                setNotes(newValue);
                requestAnimationFrame(() => {
                  const pos = selectionStart + insert.length;
                  el.setSelectionRange(pos, pos);
                  el.focus();
                });
              }
            }}
            onBlur={() => {
              const fixed = ensureLeadingMarker(notes);
              setNotes(fixed);
              const bad = countInvalidNoteLines(fixed);
              setNotesError(bad ? `Ada ${bad} baris tanpa awalan "> ".` : '');
            }}
            placeholder={`> Item 1 BGS-BG-55905: cucinya jangan cepat-cepat\n> Item 2 BGS-BG-55905: usahakan retouch; kalau tidak bisa hubungi saya dulu untuk biayanya`}
          />

          {notesError ? (
            <div className="invalid-feedback">{notesError}</div>
          ) : (
            <div className="form-text">
              Contoh: <code>&gt; Item 1 ...</code> lalu Enter → otomatis <code>&gt; </code> untuk baris berikutnya.
            </div>
          )}

          <div className="mt-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                const el = notesRef.current;
                const v = notes || '';
                const needsNewline = v && !v.endsWith('\n');
                const next = (needsNewline ? v + '\n' : v) + NOTES_MARK;
                setNotes(next);
                requestAnimationFrame(() => {
                  if (!el) return;
                  el.focus();
                  el.setSelectionRange(next.length, next.length);
                });
              }}
            >
              + Tambah baris “&gt; ”
            </button>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="card">
        <div className="card-body">
          <h5 className="mb-3">Order Items</h5>
          {rows.length ? (
            <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>SKU</th>
                    <th>Brand</th>
                    <th>Product Category</th>
                    <th>Order Category</th>
                    <th>Colour</th>
                    <th>Material</th>
                    <th style={{ width: 120 }}>Layanan</th>
                    <th style={{ width: 150 }}>
                      Initial / unit <InfoTip title={TIPS.initialColHint} />
                    </th>
                    <th style={{ width: 200 }}>
                      Final / unit (admin base) <InfoTip title={TIPS.finalColHint} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i}>
                      <td>{i + 1}</td>
                      <td className="text-monospace">{r.sku}</td>
                      <td>{r.brand_name || '—'}</td>
                      <td>{r.category_name || '—'}</td>
                      <td>{r.order_category_name || '—'}</td>
                      <td>{r.colour_name || '—'}</td>
                      <td>{r.material_name || '—'}</td>
                      <td>
                        <span className="badge badge-light text-dark">
                          {r.order_type === 'priority' ? 'Priority' : 'Regular'}
                        </span>
                      </td>
                      <td className="text-nowrap" title={TIPS.initial}>{fmtIDR(r.initial_charged)}</td>
                      <td>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control form-control-sm"
                          value={r.final_base_text}
                          onChange={(e) =>
                            setRows(prev =>
                              prev.map((x, idx) =>
                                idx === i ? { ...x, final_base_text: normalizeMoneyInput(e.target.value).text } : x
                              )
                            )
                          }
                          placeholder="1.000.000"
                          title={TIPS.final}
                        />
                        <small className="text-muted d-block">
                          Charged: {fmtIDR(effectiveFinal(r))} ({r.order_type === 'priority'
                            ? '1.5 × (base + 250k)'
                            : 'base + 250k'})
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={8} className="text-right">Subtotal</th>
                    <th className="text-nowrap">{fmtIDR(totals.init)}</th>
                    <th className="text-nowrap">{fmtIDR(totals.fin)}</th>
                  </tr>
                  {voucherDiscount > 0 && (
                    <tr>
                      <th colSpan={9} className="text-right">Discount (Voucher)</th>
                      <th className="text-nowrap">−{fmtIDR(voucherDiscount)}</th>
                    </tr>
                  )}
                  <tr>
                    <th colSpan={9} className="text-right">Total After Discount</th>
                    <th className="text-nowrap">{fmtIDR(finalAfterDiscount)}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : <div className="text-muted">No items</div>}
        </div>
      </div>
    </div>
  );
}
